// What both the server and the screens need about persons: limits, the shapes
// of the REST answers, and how external ids are written. No dependencies
// (`@trameria/core/persons-shared`).
//
// Promise:
// - External ids are kept in one form per kind: slack upper case (scope = its
//   workspace id), email lower case, github lower case without a leading `@`,
//   phone without spaces, `-` and brackets, url as an http(s) URL.
// - `looseExternalValue` writes a search word the same way without refusing it.

export const PERSON_EXTERNAL_KINDS = [
  "slack",
  "email",
  "phone",
  "github",
  "url",
] as const;
export type PersonExternalKind = (typeof PERSON_EXTERNAL_KINDS)[number];

export const PERSON_NAME_MAX = 200;
export const PERSON_ALIAS_MAX = 200;
export const PERSON_ALIASES_MAX = 20;
export const PERSON_SUMMARY_MAX = 200;
export const PERSON_NOTE_MAX = 10000;
export const PERSON_APPEND_NOTE_MAX = 2000;
export const PERSON_EXTERNAL_IDS_MAX = 20;
export const PERSON_EXTERNAL_VALUE_MAX = 500;
export const PERSON_EXTERNAL_SCOPE_MAX = 100;

export interface PersonExternalId {
  id: string;
  kind: PersonExternalKind;
  scope: string;
  value: string;
  /** A link to open it, or null. */
  url: string | null;
}

export interface PersonLabelRef {
  id: string;
  name: string;
}

export interface PersonSummary {
  id: string;
  name: string;
  aliases: string[];
  summary: string;
  /** The trameria account it is linked to (cloud shows who it is). */
  linkedUserId: string | null;
  externalIds: PersonExternalId[];
  labels: PersonLabelRef[];
  /** Ids of the workspaces (cloud's) it belongs to. */
  workspaceIds: string[];
  /** Open waitings (in open Cases) on this person. */
  waitingCount: number;
  /** When a Case last touched them (else when the person last changed). */
  lastTouchedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PersonDetail extends PersonSummary {
  note: string;
  noteRevision: number;
  source: "agent" | "manual";
  agentMemo: { body: string; version: number; logCount: number };
  waits: {
    caseId: string;
    caseTitle: string;
    stepId: string;
    title: string;
    waitUntil: string | null;
  }[];
  openCases: { caseId: string; title: string; updatedAt: string }[];
  history: {
    caseId: string;
    caseTitle: string;
    stepId: string | null;
    title: string;
    status: "open" | "done" | "dropped";
    closedAt: string | null;
  }[];
}

export interface PersonList {
  items: (PersonSummary & { match?: "external" | "exact" | "partial" })[];
  nextCursor: string | null;
}

export class ExternalIdError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExternalIdError";
  }
}

const isHttpUrl = (s: string) => {
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

/** The stored form of an external id. Throws ExternalIdError when it cannot be one. */
export function normalizeExternalId(input: {
  kind: PersonExternalKind;
  scope?: string;
  value: string;
}): { kind: PersonExternalKind; scope: string; value: string } {
  const raw = input.value.trim();
  switch (input.kind) {
    case "slack": {
      const scope = (input.scope ?? "").trim().toUpperCase();
      const value = raw.toUpperCase();
      if (!/^[A-Z0-9]+$/.test(scope) || !/^[A-Z0-9]+$/.test(value))
        throw new ExternalIdError(
          "Slack は scope にワークスペース ID、value にユーザー ID(英数字)を渡します",
        );
      return { kind: "slack", scope, value };
    }
    case "email": {
      const value = raw.toLowerCase();
      if (!value.includes("@"))
        throw new ExternalIdError("メールアドレスの形ではありません");
      return { kind: "email", scope: "", value };
    }
    case "github": {
      const value = raw.replace(/^@+/, "").toLowerCase();
      if (value === "")
        throw new ExternalIdError("GitHub のユーザー名が空です");
      return { kind: "github", scope: "", value };
    }
    case "phone": {
      const value = raw.replace(/[\s\-()]/g, "");
      if (value === "") throw new ExternalIdError("電話番号が空です");
      return { kind: "phone", scope: "", value };
    }
    case "url": {
      if (!isHttpUrl(raw))
        throw new ExternalIdError("URL は http(s) のものだけです");
      return { kind: "url", scope: "", value: raw };
    }
  }
}

export function looseExternalValue(
  kind: PersonExternalKind,
  q: string,
): string {
  switch (kind) {
    case "slack":
      return q.toUpperCase();
    case "email":
      return q.toLowerCase();
    case "github":
      return q.replace(/^@+/, "").toLowerCase();
    case "phone":
      return q.replace(/[\s\-()]/g, "");
    case "url":
      return q;
  }
}

export function externalIdUrl(e: {
  kind: PersonExternalKind;
  scope: string;
  value: string;
}): string | null {
  switch (e.kind) {
    case "slack":
      return `slack://user?team=${encodeURIComponent(e.scope)}&id=${encodeURIComponent(e.value)}`;
    case "email":
      return `mailto:${e.value}`;
    case "phone":
      return `tel:${e.value}`;
    case "github":
      return `https://github.com/${encodeURIComponent(e.value)}`;
    case "url":
      return e.value;
  }
}

/** Aliases trimmed, without empty ones, duplicates (any case) and the name. */
export function cleanAliases(
  aliases: readonly string[],
  name: string,
): string[] {
  const seen = new Set([name.trim().toLowerCase()]);
  const out: string[] = [];
  for (const raw of aliases) {
    const a = raw.trim();
    if (a === "") continue;
    const key = a.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}
